import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCallCenterContext } from "@/context/call.center.context";
import { CALLCENTER_USER_STATUS_NOT_AVAILABLE, CALLCENTER_USER_STATUS_READY } from "@icebergeradmin/common";
import { Check, ChevronDown, X } from "lucide-react";
import React from "react";

type StatusOption = {
  value: number;
  label: string;
  icon: React.ReactNode;
};

const statusOptions: StatusOption[] = [
  {
    value: CALLCENTER_USER_STATUS_READY,
    label: "I'm Ready!",
    icon: (
      <div className="grid h-5 w-5 place-items-center rounded-full bg-accent-green/15 ring-1 ring-accent-green/40">
        <Check className="h-3 w-3 text-accent-green" />
      </div>
    ),
  },
  {
    value: CALLCENTER_USER_STATUS_NOT_AVAILABLE,
    label: "Not Available",
    icon: (
      <div className="grid h-5 w-5 place-items-center rounded-full bg-accent-red/15 ring-1 ring-accent-red/40">
        <X className="h-3 w-3 text-accent-red" />
      </div>
    ),
  },
];

export function StatusDropdown() {
  const { agentStatus, isAgentAvailable, updateAgentStatus } = useCallCenterContext();
  let currentOption = statusOptions.find((option) => option.value === agentStatus);
  // For statuses without a selectable option (IN_CALL, OFFLINE, initial 0),
  // derive the label from the SAME source of truth the Join Call gate uses
  // (`isAgentAvailable`). Falling back to "I'm Ready!" unconditionally made the
  // header claim the agent was ready while the join gate rejected them — the
  // two must never contradict each other.
  if (!currentOption) {
    const fallbackValue = isAgentAvailable
      ? CALLCENTER_USER_STATUS_READY
      : CALLCENTER_USER_STATUS_NOT_AVAILABLE;
    currentOption = statusOptions.find((i) => i.value === fallbackValue);
  }

  const handleStatusChange = (newStatus?: number) => {
    updateAgentStatus(newStatus);
  };

  if (typeof agentStatus !== "number") return <div className="text-xs">loading...</div>;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="flex min-w-[130px] items-center justify-between gap-2 rounded-xl border-white/[0.06] bg-white/[0.02] px-3 py-2 text-slate-200 ring-0 transition hover:border-white/[0.14] hover:bg-white/[0.04] hover:text-white focus-visible:outline-none focus-visible:ring-0"
        >
          <div className="flex items-center gap-2">
            {currentOption?.icon}
            <span className="text-[12px] font-medium">{currentOption?.label}</span>
          </div>
          <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="min-w-[150px] rounded-xl border-white/[0.08] bg-ink-800/95 p-1 text-slate-200 shadow-card backdrop-blur-xl"
        align="start"
      >
        {statusOptions.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => handleStatusChange(option.value)}
            className="cursor-pointer rounded-lg px-3 py-2 focus:bg-white/[0.06] focus:text-white"
          >
            <div className="flex items-center gap-2">
              {option.icon}
              <span className="text-[12px] xl:text-sm whitespace-nowrap">{option.label}</span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
